/**
 * Academic Plan of Study, multi-year course roadmap tied to a chosen pathway.
 *
 * Build program §16-18: Grade × subject grid, deterministic state, "Why this
 * course?" explainability, one-click course-change requests to counselor.
 *
 * Zero AI. All state, gaps, and explanations come from the backend.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { AppLayout } from "../components/Layout";
import { toast } from "sonner";
import {
  BookOpen, CheckCircle2, Circle, Clock, Sparkles, HelpCircle,
  AlertTriangle, GraduationCap, Loader2, RefreshCw, Send, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_META = {
  completed:   { label: "Completed",   icon: CheckCircle2, cls: "bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/40" },
  in_progress: { label: "In Progress", icon: Clock,        cls: "bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/40" },
  planned:     { label: "Planned",     icon: Circle,       cls: "bg-sky-500/15 text-sky-200 ring-1 ring-sky-400/40" },
  recommended: { label: "Recommended", icon: Sparkles,     cls: "bg-fuchsia-500/15 text-fuchsia-200 ring-1 ring-fuchsia-400/40" },
  missing:     { label: "Missing",     icon: AlertTriangle,cls: "bg-rose-500/20 text-rose-200 ring-1 ring-rose-400/40" },
};

const KIND_META = {
  required: { label: "Required",   cls: "bg-slate-700 text-slate-200" },
  pathway:  { label: "Pathway",    cls: "bg-cyan-500/25 text-cyan-100" },
  elective: { label: "Elective",   cls: "bg-slate-700 text-slate-300" },
};

function CourseCard({ course, onSetState, onRequest, busy }) {
  const meta = STATE_META[course.state] || STATE_META.recommended;
  const Icon = meta.icon;
  const kindMeta = KIND_META[course.kind] || KIND_META.elective;
  const [expanded, setExpanded] = useState(false);
  const isTerminal = course.state === "completed";

  return (
    <div className={`rounded-xl border border-slate-800 bg-slate-900/60 p-3 space-y-2
                     ${course.state === "in_progress" ? "ring-2 ring-amber-400/30" : ""}
                     ${course.state === "recommended" ? "border-dashed" : ""}`}
         data-testid={`course-${course.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-bold ${kindMeta.cls}`}>
              {kindMeta.label}
            </span>
            <span className="text-[10px] text-slate-300">{course.subject}</span>
          </div>
          <div className="text-sm font-bold text-white mt-1 leading-tight">{course.title}</div>
          <div className="text-[10px] text-slate-300 mt-0.5">{course.credits} credit{course.credits === 1 ? "" : "s"}</div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0 ${meta.cls}`}>
          <Icon className={`w-3 h-3 ${course.state === "in_progress" ? "animate-pulse" : ""}`} />
          {meta.label}
        </span>
      </div>

      {course.requested && (
        <div className="text-[10px] text-sky-300 flex items-center gap-1">
          <Send className="w-2.5 h-2.5" />
          Request sent to counselor
        </div>
      )}

      <button onClick={() => setExpanded(v => !v)}
              data-testid={`why-${course.id}`}
              className="text-[10px] text-slate-300 hover:text-slate-200 inline-flex items-center gap-1 uppercase tracking-wide">
        <HelpCircle className="w-3 h-3" />
        {expanded ? "Hide reason" : "Why this course?"}
      </button>
      {expanded && (
        <div className="text-xs text-slate-300 bg-slate-800/60 rounded p-2 border-l-2 border-cyan-400">
          {course.why}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-1 pt-1">
        {!isTerminal && course.state !== "in_progress" && (
          <button onClick={() => onSetState(course.id, "completed")} disabled={busy}
                  data-testid={`mark-completed-${course.id}`}
                  className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 ring-1 ring-emerald-400/30">
            Mark done
          </button>
        )}
        {course.state === "recommended" && (
          <>
            <button onClick={() => onSetState(course.id, "planned")} disabled={busy}
                    data-testid={`mark-planned-${course.id}`}
                    className="text-[10px] px-2 py-0.5 rounded bg-sky-500/15 text-sky-200 hover:bg-sky-500/25 ring-1 ring-sky-400/30">
              Plan
            </button>
            {!course.requested && (
              <button onClick={() => onRequest(course.id)} disabled={busy}
                      data-testid={`request-${course.id}`}
                      className="text-[10px] px-2 py-0.5 rounded bg-fuchsia-500/20 text-fuchsia-200 hover:bg-fuchsia-500/30 ring-1 ring-fuchsia-400/30">
                Request from counselor
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function GradeColumn({ grade, courses, onSetState, onRequest, busy }) {
  const total = courses.length;
  const done = courses.filter(c => c.state === "completed").length;
  const inProg = courses.filter(c => c.state === "in_progress").length;
  return (
    <div className="rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-900/60 to-slate-950/60 p-3"
         data-testid={`grade-${grade}`}>
      <div className="flex items-center justify-between mb-3 px-1">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-300 font-semibold">Grade</div>
          <div className="text-2xl font-black text-white">{grade}</div>
        </div>
        <div className="text-right text-xs text-slate-300">
          <div className="text-emerald-300 font-bold">{done}/{total} done</div>
          {inProg > 0 && <div className="text-amber-300">{inProg} in progress</div>}
        </div>
      </div>
      <div className="space-y-2">
        {courses.map((c) => (
          <CourseCard key={c.id} course={c}
                      onSetState={onSetState} onRequest={onRequest} busy={busy} />
        ))}
      </div>
    </div>
  );
}

export default function AcademicPlanPage() {
  const { getAuthHeader } = useAuth();
  const [plan, setPlan]       = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [showSwitch, setShowSwitch] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/academic-plan`,         getAuthHeader()),
        axios.get(`${API}/academic-plan/catalog`, getAuthHeader()),
      ]);
      setPlan(p.data);
      setCatalog(c.data);
    } catch (e) {
      toast.error("Couldn't load academic plan");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const setCourseState = async (id, state) => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/academic-plan/course/${id}`, { state }, getAuthHeader());
      setPlan(r.data);
      toast.success(`Marked ${state.replace("_", " ")}`);
    } catch (e) {
      toast.error("Update failed", { description: e.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const requestCourse = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/academic-plan/course-request`,
                       { course_slot_id: id }, getAuthHeader());
      await load();
      toast.success("Request sent to your counselor");
    } catch (e) {
      toast.error("Couldn't send request", { description: e.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const switchPathway = async (key) => {
    setBusy(true);
    try {
      await axios.post(`${API}/academic-plan/pathway/${key}`, {}, getAuthHeader());
      await load();
      setShowSwitch(false);
      toast.success(`Pathway switched, completed courses preserved.`);
    } catch (e) {
      toast.error("Switch failed", { description: e.response?.data?.detail });
    } finally { setBusy(false); }
  };

  if (loading || !plan) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
      </div>
    );
  }

  const s = plan.summary || {};
  return (
    <AppLayout>
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"
         data-testid="academic-plan-page">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 space-y-6">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">Academic Plan</h1>
            <div className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-300 bg-emerald-500/10 ring-1 ring-emerald-400/30 rounded px-2 py-0.5 capitalize">
              {plan.pathway.replace("_"," ")} pathway
            </div>
          </div>
          <button onClick={() => setShowSwitch(v => !v)}
                  data-testid="switch-pathway-btn"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-slate-800 text-slate-200 hover:bg-slate-700 font-semibold text-sm ring-1 ring-slate-700">
            <RefreshCw className="w-4 h-4" /> Switch pathway
          </button>
        </div>

        {/* Pathway switcher */}
        {showSwitch && (
          <div className="rounded-2xl border border-slate-700 bg-slate-900/80 p-4"
               data-testid="pathway-switcher">
            <div className="text-sm text-slate-300 mb-3">
              Pick a new pathway. Your completed courses will be preserved, only
              future pathway courses change.
            </div>
            <div className="flex flex-wrap gap-2">
              {(catalog?.pathways || []).map((p) => (
                <button key={p.key} onClick={() => switchPathway(p.key)}
                        disabled={busy || p.key === plan.pathway}
                        data-testid={`switch-to-${p.key}`}
                        className={`px-3 py-1.5 rounded-lg text-sm font-semibold ring-1 transition-colors ${
                          p.key === plan.pathway
                            ? "bg-emerald-500/25 text-emerald-100 ring-emerald-400/40"
                            : "bg-slate-800 text-slate-200 hover:bg-slate-700 ring-slate-700"
                        }`}>
                  {p.label}
                  <span className="ml-1.5 opacity-60 text-xs">{p.course_count} courses</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard label="Overall"          value={`${s.completion_pct}%`}         icon={GraduationCap} accent="emerald" />
          <SummaryCard label="Pathway"          value={`${s.pathway_completion_pct}%`} icon={Sparkles}      accent="cyan" />
          <SummaryCard label="Credits Earned"   value={s.credits_completed}            icon={CheckCircle2}  accent="sky" />
          <SummaryCard label="Graduation Gaps"  value={(s.graduation_gaps || []).length} icon={AlertTriangle}
                       accent={s.on_track_for_graduation ? "emerald" : "rose"} />
        </div>

        {/* Graduation gap banner */}
        {s.graduation_gaps && s.graduation_gaps.length > 0 && (
          <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3"
               data-testid="graduation-gap-banner">
            <div className="flex items-center gap-2 text-rose-200 font-semibold text-sm mb-2">
              <AlertTriangle className="w-4 h-4" />
              Graduation credits still needed
            </div>
            <div className="flex flex-wrap gap-2">
              {s.graduation_gaps.map((g) => (
                <span key={g.subject}
                      className="text-xs px-2 py-1 rounded bg-rose-500/20 text-rose-100 ring-1 ring-rose-400/40">
                  {g.subject}: need {g.short_by} more
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Four grade columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {["9", "10", "11", "12"].map((g) => (
            <GradeColumn key={g} grade={g}
                         courses={plan.grades[g] || []}
                         onSetState={setCourseState}
                         onRequest={requestCourse}
                         busy={busy} />
          ))}
        </div>
      </div>
    </div>
    </AppLayout>
  );
}

function SummaryCard({ label, value, icon: Icon, accent }) {
  const rings = {
    emerald: "ring-emerald-500/30 bg-emerald-500/10 text-emerald-200",
    cyan:    "ring-cyan-500/30 bg-cyan-500/10 text-cyan-200",
    sky:     "ring-sky-500/30 bg-sky-500/10 text-sky-200",
    rose:    "ring-rose-500/30 bg-rose-500/10 text-rose-200",
  };
  return (
    <div className={`rounded-xl ring-1 backdrop-blur px-4 py-3 ${rings[accent]}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide opacity-80">{label}</span>
        <Icon className="w-4 h-4 opacity-80" />
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
